/**
 * @file OverviewTasksSection.tsx
 * @description «Задачи по клиенту» на «Обзоре» (phase19.31, SC68): открытые
 * задачи, звонки и встречи с флажком выполнения и «+ Задача».
 *
 * Флажок — общий `Checkbox` в строке-подписи 48px (касание по всей строке);
 * выполнение — существующий запрос `completeActivity`, после него сводка
 * перечитывается. Срок: просроченный — красным с треугольником (не только
 * цветом), иначе «до …». «Все задачи →» — вкладка «Задачи».
 */

import { Plus, TriangleAlert } from "lucide-react";

import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { SectionCard } from "../../ui/SectionCard";
import { ACTIVITY_KIND_LABEL } from "../../../lib/api-crm";
import type { CustomerOverviewTaskDto } from "../../../lib/api-customers-overview";
import { taskDueText } from "./overview-view";
import { cn } from "@/lib/utils";

interface Props {
  tasks: CustomerOverviewTaskDto[];
  total: number;
  completing: ReadonlySet<string>;
  /** Нет — флажков и «+ Задача» нет (нет права работать с CRM). */
  onComplete?: (taskId: string) => void;
  onAdd?: () => void;
}

export function OverviewTasksSection({ tasks, total, completing, onComplete, onAdd }: Props) {
  return (
    <SectionCard title="Задачи по клиенту" link={total > 0 ? { label: "Все задачи", to: "?tab=tasks" } : undefined}>
      {tasks.length === 0 ? (
        <p className="type-body text-on-surface-variant">Открытых задач нет</p>
      ) : (
        <ul aria-label="Открытые задачи клиента" className="flex flex-col divide-y divide-[var(--border)]">
          {tasks.map((t) => (
            <li key={t.id}>
              <label className={cn("flex min-h-12 items-start gap-3 py-2", onComplete ? "cursor-pointer" : "")}>
                {onComplete ? (
                  <Checkbox
                    className="mt-1"
                    checked={false}
                    disabled={completing.has(t.id)}
                    onCheckedChange={(checked) => checked === true && onComplete(t.id)}
                  />
                ) : null}
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="type-body break-words text-on-surface">{t.title}</span>
                  <span className="type-caption flex flex-wrap items-center gap-x-2 text-on-surface-variant">
                    <span>{ACTIVITY_KIND_LABEL[t.kind]}</span>
                    <span
                      className={cn("inline-flex items-center gap-1", t.overdue && "font-medium text-[var(--status-error-fg)]")}
                    >
                      {t.overdue ? <TriangleAlert aria-hidden className="size-3.5" /> : null}
                      {taskDueText(t)}
                    </span>
                    {/* Исполнитель всегда виден: «Без исполнителя» — сигнал распределить (crm.md T5). */}
                    <span>{t.assigneeName ?? "Без исполнителя"}</span>
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
      {onAdd ? (
        <Button variant="outline" className="self-start" onClick={onAdd}>
          <Plus className="size-4" aria-hidden />
          Задача
        </Button>
      ) : null}
    </SectionCard>
  );
}
